// src/components/Others/Contact.jsx
import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaPaperPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [submitStatus, setSubmitStatus] = useState(null);
    const recaptchaRef = useRef();

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value.trim()) return 'Name is required';
                if (value.length < 2) return 'Name must be at least 2 characters';
                if (value.length > 100) return 'Name must be less than 100 characters';
                return '';
            case 'email':
                if (!value.trim()) return 'Email is required';
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return 'Please enter a valid email address';
                if (value.length > 100) return 'Email must be less than 100 characters';
                return '';
            case 'message':
                if (!value.trim()) return 'Message is required';
                if (value.length < 10) return 'Message must be at least 10 characters';
                if (value.length > 1000) return 'Message must be less than 1000 characters';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
        const error = validateField(name, value);
        setErrors({ ...errors, [name]: error });
        if (submitStatus) setSubmitStatus(null);
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
        if (errors.captcha) setErrors({ ...errors, captcha: '' });
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        if (!captchaToken) newErrors.captcha = 'Please complete the CAPTCHA verification';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const url = 'https://06mxfgqqd4.execute-api.us-west-2.amazonaws.com/prod/contact';
            const response = await axios.post(url, {
                ...formData,
                captchaToken
            });

            setFormData({ name: '', email: '', message: '' });
            setCaptchaToken(null);
            setErrors({});
            recaptchaRef.current.reset();
            setSubmitStatus({ type: 'success', message: 'Thank you! We\'ll contact you within 24 hours.' });

        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Error sending message. Please try again.';
            setSubmitStatus({ type: 'error', message: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getInputClassName = (fieldName) => {
        const baseClass = "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors";
        if (errors[fieldName]) {
            return `${baseClass} border-red-300 focus:ring-red-500`;
        }
        return `${baseClass} border-gray-300 focus:ring-blue-500`;
    };

    return (
        <div className="min-h-screen bg-gray-50 py-16">
            <Helmet>
                <title>Contact Contract To Keys — Real Estate Closing Software</title>
                <meta name="description" content="Contact Contract To Keys to learn how our real estate transaction management software helps agents and transaction coordinators close deals on time." />
                <link rel="canonical" href="https://contracttokeys.com/contact" />
            </Helmet>
            <div className="max-w-2xl mx-auto px-4">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
                    <p className="text-lg text-gray-600">
                        Get in touch to learn how Contract To Keys can streamline your real estate transactions
                    </p>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-8">
                    {submitStatus && (
                        <div className={`mb-6 p-4 rounded-lg flex items-center ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                            {submitStatus.type === 'success' ? (
                                <FaCheckCircle className="h-5 w-5 mr-2" />
                            ) : (
                                <FaExclamationCircle className="h-5 w-5 mr-2" />
                            )}
                            {submitStatus.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className={getInputClassName('name')}
                                placeholder="Enter your full name"
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <FaExclamationCircle className="h-4 w-4 mr-1" />
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className={getInputClassName('email')}
                                placeholder="Enter your email address"
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <FaExclamationCircle className="h-4 w-4 mr-1" />
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                            <textarea
                                value={formData.message}
                                onChange={(e) => handleInputChange('message', e.target.value)}
                                rows="5"
                                className={getInputClassName('message')}
                                placeholder="Tell us about your real estate business and how we can help..."
                            />
                            <div className="mt-1 flex justify-between items-center">
                                <div>
                                    {errors.message && (
                                        <p className="text-sm text-red-600 flex items-center">
                                            <FaExclamationCircle className="h-4 w-4 mr-1" />
                                            {errors.message}
                                        </p>
                                    )}
                                </div>
                                <p className={`text-sm ${formData.message.length > 900 ? 'text-red-500' : 'text-gray-500'}`}>
                                    {formData.message.length}/1000 characters
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col items-center">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                onChange={handleCaptchaChange}
                            />
                            {errors.captcha && (
                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                    <FaExclamationCircle className="h-4 w-4 mr-1" />
                                    {errors.captcha}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <FaPaperPlane className="ml-2 h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <div className="mt-12 text-center">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
                            <p className="text-gray-600">support@contracttokeys.com</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Time</h3>
                            <p className="text-gray-600">Within 24 hours</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Business Hours</h3>
                            <p className="text-gray-600">Mon-Fri, 9AM-6PM EST</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
